'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Staff, ServiceItem } from './types';
import {
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Scissors,
  DollarSign,
  AlertCircle,
  FileText,
  ShieldAlert
} from 'lucide-react';

export interface BookingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  staffList: Staff[];
  services: ServiceItem[];
  selectedDate: string;
  onSuccess: () => void;
}

export function BookingDrawer({
  isOpen,
  onClose,
  staffList,
  services,
  selectedDate,
  onSuccess
}: BookingDrawerProps) {
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('any');
  const [bookingDate, setBookingDate] = useState(selectedDate);
  const [bookingTime, setBookingTime] = useState('10:00');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [isOverturn, setIsOverturn] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (services.length > 0 && !selectedServiceId) {
        setSelectedServiceId(services[0].id);
      }
      setBookingDate(selectedDate);
      setBookingTime('10:00');
      setClientName('');
      setClientPhone('');
      setClientEmail('');
      setIsOverturn(false);
      setNotes('');
      setErrorMessage(null);
    }
  }, [isOpen, selectedDate, services]);

  const selectedService = services.find((s) => s.id === selectedServiceId) || services[0];

  const handleSubmit = async () => {
    if (!selectedServiceId) {
      setErrorMessage('Seleccioná al menos un servicio');
      return;
    }
    if (!clientName.trim()) {
      setErrorMessage('Ingresá el nombre del cliente');
      return;
    }
    if (!clientPhone.trim()) {
      setErrorMessage('Ingresá el teléfono del cliente');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId: selectedServiceId,
          staffId: selectedStaffId === 'any' ? undefined : selectedStaffId,
          date: bookingDate,
          time: bookingTime,
          clientName: clientName.trim(),
          clientPhone: clientPhone.trim(),
          clientEmail: clientEmail.trim() || undefined,
          isOverturn,
          notes: notes.trim() || undefined
        })
      });

      const data = await res.json();
      if (data.success) {
        onSuccess();
        onClose();
      } else {
        if (data.code === 'SLOT_NOT_AVAILABLE') {
          setErrorMessage(
            'El horario ya se encuentra ocupado. Activá la casilla "Sobreturno" abajo para agendar de todos modos.'
          );
        } else {
          setErrorMessage(data.error || 'No se pudo crear la reserva.');
        }
      }
    } catch (err: any) {
      setErrorMessage('Error de conexión con el servidor.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Nuevo Turno Manual"
      subtitle="Agendá una cita desde el panel de recepción"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={submitting}>
            Confirmar Reserva
          </Button>
        </div>
      }
    >
      {/* Alerta de error */}
      {errorMessage && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-rose-800 text-xs font-medium">
          <AlertCircle className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* 1. Selección de Servicio */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-800">
          Servicio a Realizar *
        </label>
        <Select
          value={selectedServiceId}
          onChange={(e) => setSelectedServiceId(e.target.value)}
          options={services.map((s) => ({
            value: s.id,
            label: `${s.name} (${s.duration}m - $${s.cashPrice.toLocaleString('es-AR')})`
          }))}
        />

        {selectedService && (
          <div className="flex items-center justify-between px-3 py-2 bg-[#F4F3FF]/60 rounded-xl border border-[#ECEAFF] text-xs">
            <span className="text-slate-600 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#6255F5]" />
              Duración: <strong>{selectedService.duration} min</strong>
            </span>
            <span className="text-[#6255F5] font-bold">
              ${selectedService.cashPrice.toLocaleString('es-AR')}
            </span>
          </div>
        )}
      </div>

      {/* 2. Profesional Asignado */}
      <div>
        <label className="block text-xs font-semibold text-slate-800 mb-1.5">
          Profesional
        </label>
        <Select
          value={selectedStaffId}
          onChange={(e) => setSelectedStaffId(e.target.value)}
          options={[
            { value: 'any', label: 'Cualquier profesional disponible' },
            ...staffList.map((st) => ({
              value: st.id,
              label: st.name
            }))
          ]}
        />
      </div>

      {/* 3. Fecha y Hora */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Fecha de la Cita *"
          type="date"
          value={bookingDate}
          onChange={(e) => setBookingDate(e.target.value)}
          leftIcon={<Calendar className="w-4 h-4" />}
        />

        <Input
          label="Hora de Inicio *"
          type="time"
          value={bookingTime}
          onChange={(e) => setBookingTime(e.target.value)}
          leftIcon={<Clock className="w-4 h-4" />}
        />
      </div>

      {/* 4. Datos del Cliente */}
      <div className="p-4 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-3">
        <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-[#6255F5]" />
          <span>Datos del Cliente</span>
        </p>

        <Input
          placeholder="Nombre y Apellido *"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          leftIcon={<User className="w-4 h-4" />}
        />

        <Input
          placeholder="Teléfono móvil (WhatsApp) *"
          type="tel"
          value={clientPhone}
          onChange={(e) => setClientPhone(e.target.value)}
          leftIcon={<Phone className="w-4 h-4" />}
          helperText="Se enviará la confirmación por WhatsApp a este número"
        />

        <Input
          placeholder="Correo electrónico (opcional)"
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          leftIcon={<Mail className="w-4 h-4" />}
        />
      </div>

      {/* 5. Opciones Avanzadas */}
      <div className="space-y-3 pt-1">
        <Switch
          checked={isOverturn}
          onChange={setIsOverturn}
          label="Forzar como Sobreturno"
          description="Permite crear la cita incluso si se solapa con otro turno o fuera de horario"
        />

        <div>
          <label className="block text-xs font-semibold text-slate-800 mb-1.5 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            <span>Notas internas (Opcional)</span>
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Comentarios privados para el profesional (ej: alérgico a cierto tinte)..."
            className="w-full p-3 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#6255F5] focus:ring-2 focus:ring-[#6255F5]/20 placeholder:text-slate-400"
          />
        </div>
      </div>
    </SlideOverDrawer>
  );
}
